import type { FieldErrors } from 'react-hook-form';

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function isFieldErrorLike(value: unknown): boolean {
  if (!isPlainObject(value)) return false;
  return 'ref' in value || 'type' in value || 'message' in value;
}

export function collectErrorPaths(
  errors: FieldErrors,
  prefix = '',
  visited: WeakSet<object> = new WeakSet()
): string[] {
  const paths: string[] = [];
  if (!isPlainObject(errors)) return paths;
  if (visited.has(errors)) return paths;
  visited.add(errors);

  Object.entries(errors).forEach(([key, value]) => {
    if (key === 'ref' || key === 'root') return;
    const nextPath = prefix ? `${prefix}.${key}` : key;
    if (!value) return;
    if (typeof value === 'object' && 'message' in value && value.message) {
      paths.push(nextPath);
    }
    if (isFieldErrorLike(value)) {
      return;
    }
    if (Array.isArray(value)) {
      value.forEach((entry, index) => {
        if (!entry) return;
        if (typeof entry === 'object' && 'message' in entry && entry.message) {
          paths.push(`${nextPath}.${index}`);
          return;
        }
        if (isFieldErrorLike(entry)) {
          return;
        }
        if (isPlainObject(entry)) {
          paths.push(...collectErrorPaths(entry as FieldErrors, `${nextPath}.${index}`, visited));
        }
      });
      return;
    }
    if (isPlainObject(value)) {
      paths.push(...collectErrorPaths(value as FieldErrors, nextPath, visited));
    }
  });
  return paths;
}

export function getErrorCount(errors: FieldErrors): number {
  return collectErrorPaths(errors).length;
}

export function getFirstErrorPath(errors: FieldErrors): string | null {
  const paths = collectErrorPaths(errors);
  return paths.length > 0 ? paths[0] : null;
}

function getSafeSelector(value: string): string {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
    return CSS.escape(value);
  }
  return value.replace(/([#.;?+*~':"^$\\[\\](){|}\\s])/g, '\\$1');
}

export function focusFieldByPath(path: string): boolean {
  if (!path) return false;
  const safe = getSafeSelector(path);
  const element =
    document.querySelector<HTMLElement>(`[name="${safe}"]`) ?? document.getElementById(path);
  if (!element) return false;
  element.focus?.();
  return true;
}

export function scrollToFieldByPath(path: string): boolean {
  if (!path) return false;
  const safe = getSafeSelector(path);
  const element =
    document.querySelector<HTMLElement>(`[name="${safe}"]`) ?? document.getElementById(path);
  if (!element) return false;
  element.scrollIntoView({ behavior: 'smooth', block: 'center' });
  element.focus?.();
  return true;
}

export function scrollToFirstError(errors: FieldErrors): boolean {
  const path = getFirstErrorPath(errors);
  if (!path) return false;
  return scrollToFieldByPath(path);
}

export function focusFirstError(errors: FieldErrors): boolean {
  const path = getFirstErrorPath(errors);
  if (!path) return false;
  return focusFieldByPath(path);
}
